import React, { useContext, useEffect, useState } from 'react'
import { message } from 'antd'
import { axiosInstance } from '../../../hooks/useAxios'
import { IAbilities, IUserLists, UserRole } from '../../../types/user'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import InputUpload from '../../../components/InputUpload'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import { closeModal } from '../../../../store/reducers/modal/modal.slice'
import { useDispatch, useSelector } from 'react-redux'
import { AbilitiesPermissions, UserRoleLists } from '../../../constants/user'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { useUser } from '../../../hooks/useUser'
import { AbilityContext } from '../../../components/Ability'

interface IUserFormModalProps {
  user?: IUserLists
  onUpdateUserSuccess: () => void
}

interface IUserFormModal {
  username: string
  password?: string
  confirmPassword?: string
  phoneNumber: string
  name: string
  role: UserRole
  abilities: IAbilities[]
}

export const UserFormModal = (props: IUserFormModalProps) => {
  const { merchant } = useUser()
  const ability = useContext(AbilityContext)
  const dispatch = useDispatch()
  const [isAvailable, setIsAvailable] = useState<boolean>(true)
  const [isUseAI, setIsUseAI] = useState<boolean>(true)
  const [isNotAllowDeleteItem, setIsNotAllowDeleteItem] = useState<boolean>(false)
  const [isNotAllowSeeCost, setIsNotAllowSeeCost] = useState<boolean>(false)
  const [isEnablePackingSystem, setIsEnablePackingSystem] = useState<boolean>(false)
  const [isEnableShippingSettings, setIsEnableShippingSettings] = useState<boolean>(false)
  const { isOpen } = useSelector((state: any) => state.modal)
  const { user, onUpdateUserSuccess } = props
  const [selectedImg, setSelectedImg] = useState<string | undefined>('')

  useEffect(() => {
    setSelectedImg(user?.image)
    setIsAvailable(user?.isAvailable ?? false)
    setIsUseAI(user?.roles?.includes(UserRole.AI) ?? false)
    setIsNotAllowDeleteItem(user?.isNotAllowDeleteItem ?? false)
    setIsNotAllowSeeCost(user?.isNotAllowSeeCost ?? false)
    setIsEnablePackingSystem(user?.isEnablePackingSystem ?? false)
    setIsEnableShippingSettings(user?.isEnableShippingSettings ?? false)
    formik.setValues({
      username: user?.username || '',
      password: '',
      confirmPassword: '',
      phoneNumber: user?.phoneNumber || '',
      name: user?.name || '',
      role: (user?.roles[0] as UserRole) || UserRole.USER,
      abilities: user?.abilities || [],
    })
  }, [isOpen])

  const formik = useFormik<IUserFormModal>({
    initialValues: {
      username: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      name: '',
      role: UserRole.USER,
      abilities: [],
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .required('กรุณากรอกชื่อผู้ใช้งาน')
        .matches(
          /^[a-z0-9-]+$/,
          'ชื่อผู้ใช้งานเฉพาะภาษาอังกฤษ, ตัวเลข หรือ - เท่านั้น',
        )
        .min(4, 'ชื่อผู้ใช้งานต้องมีอย่างน้อย 4 ตัวอักษร'),
      password: !!user
        ? Yup.string().notRequired()
        : Yup.string()
          .required('กรุณากรอกรหัสผ่าน')
          .min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
      confirmPassword: !!user
        ? Yup.string().notRequired()
        : Yup.string()
          .required('กรุณากรอกรหัสผ่านอีกครั้ง')
          .oneOf([Yup.ref('password'), null!], 'รหัสผ่านไม่ตรงกัน'),
      phoneNumber: Yup.string()
        .required('กรุณากรอกหมายเลขโทรศัพท์')
        .matches(
          /^[0-9]{9,10}$/,
          'กรุณากรอกหมายเลขโทรศัพท์ที่ถูกต้อง (9-10 ตัว)',
        ),
      name: Yup.string()
        .required('กรุณากรอกชื่อ - นามสกุล')
        .min(1, 'ชื่อ - นามสกุลต้องมีอย่างน้อย 1 ตัวอักษร'),
      role: Yup.string().required('กรุณาเลือกประเภทผู้ใช้งาน'),
      abilities: Yup.array().of(
        Yup.object().shape({
          action: Yup.string().required('กรุณาเลือกสิทธิ์การใช้งาน'),
          subject: Yup.string().required('กรุณาเลือกสิทธิ์การใช้งาน'),
          fields: Yup.array().of(
            Yup.string().required('กรุณาเลือกสิทธิ์การใช้งาน'),
          ),
        }),
      ),
    }),
    onSubmit: async (values: IUserFormModal) => {
      const isUpdate = !!user
      try {
        const reponse = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/users/update/${user?.username}` : '/users/register',
          data: isUpdate
            ? {
              phoneNumber: values.phoneNumber,
              name: values.name,
              image: selectedImg,
              isAvailable: isAvailable,
              roles: [values.role],
              abilities: values.abilities.filter((a) => a?.fields.length > 0),
              isUseAI: isUseAI,
              isNotAllowDeleteItem: isNotAllowDeleteItem,
              isNotAllowSeeCost: isNotAllowSeeCost,
              isEnablePackingSystem: isEnablePackingSystem,
              isEnableShippingSettings: isEnableShippingSettings,
            }
            : {
              ...values,
              image: selectedImg,
              isAvailable: isAvailable,
              roles: [values.role],
              abilities: values.abilities.filter((a) => a?.fields.length > 0),
              isUseAI: isUseAI,
              isNotAllowDeleteItem: isNotAllowDeleteItem,
              isNotAllowSeeCost: isNotAllowSeeCost,
              isEnablePackingSystem: isEnablePackingSystem,
              isEnableShippingSettings: isEnableShippingSettings,
            },
        })
        const { status } = reponse
        message.success(
          status === 201
            ? 'สร้างพนักงานสำเร็จ'
            : status === 200
              ? `แก้ไขพนักงาน ${user?.username} สำเร็จ`
              : 'บันทึกข้อมูลไม่สำเร็จ',
        )
        setSelectedImg('')
        setIsAvailable(false)
        formik.resetForm()
        dispatch(closeModal())
        onUpdateUserSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      setSelectedImg(response.data.url)
      message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
      <Grid container spacing={2} justifyContent="space-between">
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <TextField
                id="username"
                fullWidth
                disabled={!!user}
                label="กรอกชื่อผู้ใช้งาน"
                name="username"
                helperText={
                  formik.touched.username && (formik.errors.username as string)
                }
                error={!!(formik.touched.username && formik.errors.username)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.username}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="phoneNumber"
                fullWidth
                label="กรอกหมายเลขโทรศัพท์"
                name="phoneNumber"
                helperText={
                  formik.touched.phoneNumber &&
                  (formik.errors.phoneNumber as string)
                }
                error={
                  !!(formik.touched.phoneNumber && formik.errors.phoneNumber)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.phoneNumber}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="กรอกชื่อ - นามสกุล"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            {!user && (
              <>
                <Grid item xs={12} lg={6}>
                  <TextField
                    type={'password'}
                    id="password"
                    fullWidth
                    label="กรอกรหัสผ่าน"
                    name="password"
                    helperText={
                      formik.touched.password &&
                      (formik.errors.password as string)
                    }
                    error={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.password}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextField
                    type={'password'}
                    id="confirmPassword"
                    fullWidth
                    label="กรอกรหัสผ่านอีกครั้ง"
                    name="confirmPassword"
                    helperText={
                      formik.touched.confirmPassword &&
                      (formik.errors.confirmPassword as string)
                    }
                    error={
                      !!(
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      )
                    }
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.confirmPassword}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} lg={12}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="role-label">ประเภทผู้ใช้งาน</InputLabel>
                <Select
                  id="role"
                  name="role"
                  labelId="role-label"
                  value={formik.values.role}
                  onChange={(e) => {
                    formik.setFieldValue('role', e.target.value as UserRole)
                  }}
                  onBlur={formik.handleBlur}
                  fullWidth
                // disabled={!ability.can('manage', 'User', 'updateStatus')}
                >
                  {UserRoleLists.map((item, index: number) => (
                    <MenuItem value={item.value} key={index}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12}>
              <InputUpload
                handleUploadClick={handleUploadClick}
                imageUrl={selectedImg ?? ''}
              />
            </Grid>

          </Grid>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={4}>
              <label>เปิดการใช้งาน</label>
              <Switch
                checked={isAvailable}
                onChange={() => setIsAvailable(!isAvailable)}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <label>แชทบอท AI</label>
              <Switch checked={isUseAI} onChange={() => setIsUseAI(!isUseAI)} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <label>ห้ามลบสินค้าคนอื่น</label>
              <Switch checked={isNotAllowDeleteItem} onChange={() => setIsNotAllowDeleteItem(!isNotAllowDeleteItem)} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <label>ปิดการมองเห็นต่อทุนสินค้า</label>
              <Switch checked={isNotAllowSeeCost} onChange={() => setIsNotAllowSeeCost(!isNotAllowSeeCost)} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <label>เปิดใช้งานระบบแพ็คสินค้า</label>
              <Switch checked={isEnablePackingSystem} onChange={() => setIsEnablePackingSystem(!isEnablePackingSystem)} />
            </Grid>
            {/* <Grid item xs={12} lg={4}>
              <label>เปิดใช้งานตั้งค่าขนส่ง</label>
              <Switch checked={isEnableShippingSettings} onChange={() => setIsEnableShippingSettings(!isEnableShippingSettings)} />
            </Grid> */}
          </Grid>
        </Grid>
        {formik.values.role !== UserRole.ADMIN && (
          <Grid item xs={12} lg={12}>
            <Grid container spacing={2}>
              {AbilitiesPermissions.map((ability: any, index: any) => {
                if (
                  merchant.config?.openCRM === false &&
                  ability.subject === 'CRM'
                )
                  return null
                return (
                  <Grid item xs={12} lg={12} key={index}>
                    <FormControlLabel
                      label={ability.label}
                      control={
                        <Checkbox
                          hidden={true}
                          checked={formik.values.abilities.some(
                            (a) => a?.subject === ability.subject,
                          )}
                          indeterminate={formik.values.abilities.some(
                            (a) => a?.subject === ability.subject,
                          )}
                          onChange={(e) =>
                            formik.setFieldValue(
                              'abilities',
                              formik.values.abilities.some(
                                (a) => a?.subject === ability.subject,
                              )
                                ? formik.values.abilities.filter(
                                  (a) => a?.subject !== ability.subject,
                                )
                                : formik.values.abilities.concat({
                                  action: 'manage',
                                  subject: ability.subject,
                                  fields: ability.fields.map(
                                    (field: any) => field.value,
                                  ),
                                }),
                            )
                          }
                        />
                      }
                    />
                    <Grid container spacing={2}>
                      {ability.fields.map((field: any, index: number) => {
                        return (
                          field.value !== 'view' && (
                            <Grid item xs={6} lg={3} key={index}>
                              <FormControlLabel
                                key={index}
                                label={
                                  <Typography variant="body2">
                                    {field.label}
                                  </Typography>
                                }
                                control={
                                  <Checkbox
                                    checked={formik.values.abilities.some(
                                      (a) =>
                                        a?.subject === ability.subject &&
                                        a?.fields.includes(field.value),
                                    )}
                                    onChange={(e) => {
                                      const abilities =
                                        formik.values.abilities.map((a) =>
                                          a?.subject === ability.subject
                                            ? {
                                              ...a,
                                              fields: a.fields.includes(
                                                field.value,
                                              )
                                                ? a.fields.filter(
                                                  (f: string) =>
                                                    f !== field.value,
                                                )
                                                : a.fields.concat(
                                                  field.value,
                                                ),
                                            }
                                            : a,
                                        )
                                      formik.setFieldValue(
                                        'abilities',
                                        abilities.some(
                                          (a) => a?.subject === ability.subject,
                                        )
                                          ? abilities
                                          : [
                                            ...formik.values.abilities,
                                            {
                                              action: 'manage',
                                              subject: ability.subject,
                                              fields: [field.value],
                                            },
                                          ],
                                      )
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          )
                        )
                      })}
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
        <Grid item>
          <Button
            startIcon={<SaveIcon />}
            color="primary"
            type="submit"
            size="large"
            variant="contained"
          >
            {user ? 'แก้ไขพนักงาน' : 'สร้างพนักงาน'}
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="inherit"
            size="large"
            variant="contained"
            onClick={() => dispatch(closeModal())}
            startIcon={<ClearIcon />}
          >
            ปิดหน้าต่าง
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
